import type { StoreListItem } from "@/types";

/**
 * Algerian cities (wilaya capitals) used to build the SEO landing pages
 * (`/livraison/[ville]`). Each store discovered by the flood-fill is bucketed to
 * its nearest city, so these power both the directory hub and the per-city pages
 * — and give Google geo-intent pages ("livraison Alger", "restaurants Oran").
 *
 * `slug` is ASCII/kebab for clean, stable URLs; `name` is the display label.
 */
export interface City {
  slug: string;
  name: string;
  latitude: number;
  longitude: number;
  /**
   * Slug of the wilaya this zone belongs to, when the zone is one of its
   * communes. A parent zone aggregates every store of its children — without
   * this, `/livraison/alger` listed only the stores nearest to Alger-Centre
   * (15 of ~349), making the highest-volume page the weakest one on the site.
   */
  parent?: string;
}

export const CITIES: readonly City[] = [
  // ── Wilayas desservies (hors Alger, détaillée en communes plus bas) ──
  { slug: "oran", name: "Oran", latitude: 35.6969, longitude: -0.6331 },
  { slug: "annaba", name: "Annaba", latitude: 36.9, longitude: 7.7667 },
  { slug: "boumerdes", name: "Boumerdès", latitude: 36.7667, longitude: 3.4772 },
  { slug: "blida", name: "Blida", latitude: 36.4703, longitude: 2.8277 },

  // ── Alger : centre + communes (zones détaillées) ──
  // Alger s'étend sur ~40 km : chaque commune est une zone à part entière (les
  // magasins sont rattachés à la plus proche), pour cibler "livraison Hydra",
  // "livraison Bab Ezzouar", etc. "alger" reste le centre-ville.
  { slug: "alger", name: "Alger", latitude: 36.7538, longitude: 3.0588 },
  { slug: "hydra", name: "Hydra", latitude: 36.744, longitude: 3.029, parent: "alger" },
  { slug: "el-biar", name: "El Biar", latitude: 36.7689, longitude: 3.0264, parent: "alger" },
  { slug: "dely-brahim", name: "Dely Brahim", latitude: 36.7556, longitude: 2.9889, parent: "alger" },
  { slug: "bab-ezzouar", name: "Bab Ezzouar", latitude: 36.7178, longitude: 3.1869, parent: "alger" },
  { slug: "ben-aknoun", name: "Ben Aknoun", latitude: 36.7625, longitude: 3.0167, parent: "alger" },
  { slug: "bir-mourad-rais", name: "Bir Mourad Raïs", latitude: 36.7311, longitude: 3.0469, parent: "alger" },
  { slug: "kouba", name: "Kouba", latitude: 36.715, longitude: 3.09, parent: "alger" },
  { slug: "hussein-dey", name: "Hussein Dey", latitude: 36.7406, longitude: 3.095, parent: "alger" },
  { slug: "bab-el-oued", name: "Bab El Oued", latitude: 36.7944, longitude: 3.0506, parent: "alger" },
  { slug: "cheraga", name: "Chéraga", latitude: 36.7672, longitude: 2.9558, parent: "alger" },
  { slug: "ouled-fayet", name: "Ouled Fayet", latitude: 36.7456, longitude: 2.9536, parent: "alger" },
  { slug: "draria", name: "Draria", latitude: 36.7233, longitude: 2.9961, parent: "alger" },
  { slug: "birkhadem", name: "Birkhadem", latitude: 36.7097, longitude: 3.0506, parent: "alger" },
  { slug: "bordj-el-kiffan", name: "Bordj El Kiffan", latitude: 36.7472, longitude: 3.1936, parent: "alger" },
  { slug: "bordj-el-bahri", name: "Bordj El Bahri", latitude: 36.7967, longitude: 3.2542, parent: "alger" },
  { slug: "rouiba", name: "Rouiba", latitude: 36.7378, longitude: 3.2842, parent: "alger" },
  { slug: "reghaia", name: "Reghaïa", latitude: 36.7331, longitude: 3.3431, parent: "alger" },
  { slug: "dar-el-beida", name: "Dar El Beïda", latitude: 36.7136, longitude: 3.2122, parent: "alger" },
  { slug: "baraki", name: "Baraki", latitude: 36.6678, longitude: 3.0889, parent: "alger" },
  { slug: "bachdjarah", name: "Bachdjarah", latitude: 36.7242, longitude: 3.1158, parent: "alger" },
  { slug: "el-harrach", name: "El Harrach", latitude: 36.7178, longitude: 3.1356, parent: "alger" },
  { slug: "ain-benian", name: "Aïn Benian", latitude: 36.8033, longitude: 2.9211, parent: "alger" },
  { slug: "staoueli", name: "Staouéli", latitude: 36.7531, longitude: 2.8869, parent: "alger" },
  { slug: "zeralda", name: "Zéralda", latitude: 36.7106, longitude: 2.8422, parent: "alger" },
  { slug: "saoula", name: "Saoula", latitude: 36.6961, longitude: 3.0142, parent: "alger" },
  { slug: "mohammadia", name: "Mohammadia", latitude: 36.7331, longitude: 3.1411, parent: "alger" },
  { slug: "bouzareah", name: "Bouzaréah", latitude: 36.79, longitude: 3.0211, parent: "alger" },
  { slug: "el-mouradia", name: "El Mouradia", latitude: 36.7556, longitude: 3.0444, parent: "alger" },
  { slug: "ain-naadja", name: "Aïn Naâdja", latitude: 36.6928, longitude: 3.0858, parent: "alger" },
  { slug: "gue-de-constantine", name: "Gué de Constantine", latitude: 36.6961, longitude: 3.0764, parent: "alger" },
];

/**
 * Areas shown in the footer for site-wide internal linking. Curated to zones
 * that currently have stores so footer links never hit an empty (404) page —
 * Blida is intentionally omitted until it has active stores (it's still in
 * CITIES, so its page activates automatically once stores appear there).
 */
export const FEATURED_CITY_SLUGS = [
  "alger",
  "oran",
  "annaba",
  "boumerdes",
  "hydra",
  "dely-brahim",
  "bab-ezzouar",
  "cheraga",
] as const;

const BY_SLUG = new Map(CITIES.map((c) => [c.slug, c]));

const CHILDREN = new Map<string, City[]>();
for (const c of CITIES) {
  if (!c.parent) continue;
  const arr = CHILDREN.get(c.parent);
  if (arr) arr.push(c);
  else CHILDREN.set(c.parent, [c]);
}

/** Communes belonging to a wilaya zone (empty for a leaf zone). */
export function getChildCities(slug: string): City[] {
  return CHILDREN.get(slug) ?? [];
}

/** True when the zone aggregates communes (currently only Alger). */
export function isParentCity(city: City): boolean {
  return CHILDREN.has(city.slug);
}

/** Zones with no parent — the wilaya level, used for the directory hub. */
export function getRootCities(): City[] {
  return CITIES.filter((c) => !c.parent);
}

export function getCityBySlug(slug: string): City | undefined {
  return BY_SLUG.get(slug);
}

export function getFeaturedCities(): City[] {
  return FEATURED_CITY_SLUGS.map((s) => BY_SLUG.get(s)).filter(Boolean) as City[];
}

/** Great-circle distance in km between two lat/lng points. */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** City a coordinate belongs to (nearest within `maxKm`), or null if too far. */
export function nearestCity(lat: number, lng: number, maxKm = 60): City | null {
  let best: City | null = null;
  let bestD = Infinity;
  for (const c of CITIES) {
    const d = haversineKm(lat, lng, c.latitude, c.longitude);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best && bestD <= maxKm ? best : null;
}

/** StoreListItem.location.coordinates is [lng, lat]; returns [lat, lng]. */
function storeLatLng(s: StoreListItem): [number, number] | null {
  const co = s.location?.coordinates;
  return co ? [co[1], co[0]] : null;
}

/** Abréviations qu'un magasin porte dans son nom à la place de sa commune. */
const PLACE_ALIASES: Record<string, string> = { bbz: "Bab Ezzouar" };

/** Minuscules, sans accents, lettres et chiffres seulement ; « Ibrahim » ramené à
 *  « Brahim », la graphie des adresses Google face à celle du catalogue. */
function placeKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\bibrahim\b/g, "brahim")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** « BBZ » → « Bab Ezzouar » ; tout autre libellé est rendu tel quel. */
export function expandPlaceAlias(label: string): string {
  return PLACE_ALIASES[placeKey(label)] ?? label;
}

const BY_PLACE_KEY = new Map(CITIES.map((c) => [placeKey(c.name), c]));

/** Au-delà, un libellé et une adresse concordants relèveraient de l'homonymie. */
const DECLARED_CITY_MAX_KM = 5;

/**
 * La commune que le magasin déclare lui-même — « Thaisty Crousty - BBZ » —
 * retenue seulement si son adresse la confirme.
 */
function declaredCity(s: StoreListItem, lat: number, lng: number): City | null {
  const parts = s.name.split(/\s+[-–—]\s+/);
  if (parts.length < 2) return null;
  const city = BY_PLACE_KEY.get(placeKey(expandPlaceAlias(parts.slice(1).join(" "))));
  if (!city) return null;
  const address = ` ${placeKey(s.formatted_address ?? "")} `;
  if (!address.includes(` ${placeKey(city.name)} `)) return null;
  return haversineKm(lat, lng, city.latitude, city.longitude) <= DECLARED_CITY_MAX_KM ? city : null;
}

/**
 * La commune d'un magasin.
 *
 * Le centre de commune le plus proche ne suffit pas : les communes d'Alger sont
 * à 1-3 km les unes des autres, et un magasin en bordure bascule chez la
 * voisine. Mesuré sur le catalogue du 15/09 : 17 magasins nomment une commune
 * que leur adresse confirme, et le site les rangeait ailleurs — « Thaisty
 * Crousty - BBZ » (adresse : Bab Ezzouar) à Dar El Beïda, « Plan B - Hydra »
 * (adresse : Hydra) à Bir Mourad Raïs, donc absent de la page Hydra. Or
 * « thaisty crousty bab ezzouar » totalise ~2 200 impressions en 90 jours.
 *
 * Nom ET adresse concordants, à moins de 5 km : sinon, le plus proche. Un
 * magasin reste dans une seule commune, les pages voisines restent distinctes.
 */
export function cityOfStore(s: StoreListItem): City | null {
  const ll = storeLatLng(s);
  if (!ll) return null;
  return declaredCity(s, ll[0], ll[1]) ?? nearestCity(ll[0], ll[1]);
}

/**
 * Stores whose zone is exactly `city` (cf. `cityOfStore`) — the primitive assignment, one
 * store to one zone. Communes are 1–3 km apart in Algiers, so an exclusive
 * nearest-point assignment is what keeps neighbouring commune pages genuinely
 * distinct from one another (a radius-based membership was measured against the
 * live catalogue and produced 13–17 pairs of commune pages sharing >90% of
 * their stores — near-duplicates Google would rightly collapse).
 */
export function ownStoresForCity(stores: StoreListItem[], city: City): StoreListItem[] {
  return stores.filter((s) => cityOfStore(s)?.slug === city.slug);
}

/**
 * Every store a zone page should list.
 *
 * For a leaf zone (a commune, or a wilaya with no communes modelled) this is
 * its own stores. For a parent zone it is the UNION of its own stores and those
 * of all its communes — Alger contains Hydra, so `/livraison/alger` must list
 * Hydra's restaurants. Parent/child overlap is hierarchical containment, not
 * duplication: it is the sibling pages that must stay distinct.
 */
export function storesForCity(stores: StoreListItem[], city: City): StoreListItem[] {
  const children = getChildCities(city.slug);
  if (!children.length) return ownStoresForCity(stores, city);

  const byUlid = new Map<string, StoreListItem>();
  for (const s of stores) {
    const nearest = cityOfStore(s)?.slug;
    if (!nearest) continue;
    if (nearest === city.slug || children.some((c) => c.slug === nearest)) {
      byUlid.set(s.ulid, s);
    }
  }
  return [...byUlid.values()];
}

/**
 * Aggregate stats for a set of stores — used to build unique, data-driven copy
 * per landing page (so pages aren't templated near-duplicates) and to answer
 * the FAQ / AI engines with real numbers.
 */
export function storeStats(stores: StoreListItem[]): {
  count: number;
  avgRating: number;
  avgTime: number;
} {
  const rated = stores.filter((s) => s.rating > 0);
  const timed = stores.filter((s) => s.avg_time > 0);
  const avgRating = rated.length
    ? Math.round((rated.reduce((a, s) => a + s.rating, 0) / rated.length) * 10) / 10
    : 0;
  const avgTime = timed.length
    ? Math.round(timed.reduce((a, s) => a + s.avg_time, 0) / timed.length)
    : 0;
  return { count: stores.length, avgRating, avgTime };
}

/**
 * How many reviews a store really has. `reviews_count` on the catalogue
 * endpoints is 0 for every store, so a warmed summary map is the only source
 * that can break a tie between two 5-star shops.
 */
type ReviewCounts = Record<string, { average: number; total: number }>;

function reviewTotal(s: StoreListItem, reviews?: ReviewCounts): number {
  return reviews?.[s.ulid]?.total ?? s.reviews_count ?? 0;
}

/** Top stores by rating (then review count) — for unique per-page content. */
export function topStores(
  stores: StoreListItem[],
  n = 3,
  reviews?: ReviewCounts,
): StoreListItem[] {
  return [...stores]
    .sort(
      (a, b) => b.rating - a.rating || reviewTotal(b, reviews) - reviewTotal(a, reviews),
    )
    .slice(0, n);
}

/**
 * Best stores to surface/recommend: well-rated AND with enough reviews to be
 * credible (avoids a 5★/1-review store outranking a 4.7★/300-review one). Falls
 * back to plain rating order if too few have enough reviews.
 */
export function bestStores(
  stores: StoreListItem[],
  n = 10,
  reviews?: ReviewCounts,
  minReviews = 5,
): StoreListItem[] {
  const credible = stores.filter((s) => s.rating > 0 && reviewTotal(s, reviews) >= minReviews);
  const pool = credible.length >= Math.min(n, 3) ? credible : stores.filter((s) => s.rating > 0);
  return [...pool]
    .sort(
      (a, b) => b.rating - a.rating || reviewTotal(b, reviews) - reviewTotal(a, reviews),
    )
    .slice(0, n);
}

/** Comma-joined cuisine/category names of a store, for descriptions. */
export function storeCuisines(s: StoreListItem): string {
  return (s.appCategories ?? []).map((c) => c.name).join(", ");
}

/**
 * Partition every store into its nearest zone — one pass, exclusive buckets.
 * Use `zoneStoresFromBuckets` on top of this to get a parent zone's full list.
 */
export function bucketStoresByCity(stores: StoreListItem[]): Map<string, StoreListItem[]> {
  const buckets = new Map<string, StoreListItem[]>();
  for (const s of stores) {
    const c = cityOfStore(s);
    if (!c) continue;
    const arr = buckets.get(c.slug);
    if (arr) arr.push(s);
    else buckets.set(c.slug, [s]);
  }
  return buckets;
}

/**
 * A zone's full store list, derived from the exclusive buckets in one pass —
 * the cheap counterpart of `storesForCity` when every zone is being sized at
 * once (the directory hub and the sitemap).
 */
export function zoneStoresFromBuckets(
  buckets: Map<string, StoreListItem[]>,
  city: City,
): StoreListItem[] {
  const own = buckets.get(city.slug) ?? [];
  const children = getChildCities(city.slug);
  if (!children.length) return own;

  const byUlid = new Map<string, StoreListItem>();
  for (const s of own) byUlid.set(s.ulid, s);
  for (const child of children) {
    for (const s of buckets.get(child.slug) ?? []) byUlid.set(s.ulid, s);
  }
  return [...byUlid.values()];
}

/**
 * Zones geographically closest to `city`, for lateral internal linking
 * ("Sushi à Hydra" → "Sushi à Ben Aknoun"). Restricted to siblings — a commune
 * links to communes, a wilaya to wilayas — so the mesh never jumps a level.
 */
export function nearbyCities(city: City, limit = 5): City[] {
  return CITIES.filter((c) => c.slug !== city.slug && c.parent === city.parent)
    .map((c) => ({ c, d: haversineKm(city.latitude, city.longitude, c.latitude, c.longitude) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map((x) => x.c);
}

/** The wilaya a commune belongs to, if any. */
export function getParentCity(city: City): City | undefined {
  return city.parent ? BY_SLUG.get(city.parent) : undefined;
}

/**
 * French elision for a zone name: "d'Alger", "de Kouba".
 *
 * Generated copy is the text an answer engine quotes back verbatim, so it has
 * to be grammatical. Elides before a vowel and before h — "d'Hydra",
 * "d'Hussein Dey" — which is standard for these names.
 */
export function cityDe(name: string): string {
  const first = name.trim().charAt(0).normalize("NFD").charAt(0).toLowerCase();
  return /[aeiouyh]/.test(first) ? `d'${name}` : `de ${name}`;
}
